import React, { useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useAppDispatch } from '../store/hooks';
import { initializeAuth } from '../store/slices/authSlice';
import LoadingScreen from './LoadingScreen';

// Components
import AdminRoute from './AdminRoute';
import UserRoute from './UserRoute';
import DashboardLayout from './DashboardLayout';

// Pages
import Home from '../pages/Home';
import Login from '../pages/Login';
import Register from '../pages/Register';
import Profile from '../pages/Profile';
import UserDashboard from '../pages/UserDashboard';
import SummaryView from '../pages/SummaryView';
import ProcessesPage from '../pages/ProcessesPage';
import Feedback from '../pages/Feedback';
import AdminDashboard from '../pages/AdminDashboard';
import UserManagement from '../pages/UserManagement';
import MonitoringDashboard from '../pages/MonitoringDashboard';
import SummariesPage from '../pages/SummariesPage';
import WeeklySummariesPage from '../pages/WeeklySummariesPage';
import GettingStarted from '../pages/GettingStarted';
import CustomerProfiling from '../pages/CustomerProfiling';
import SsoCallback from '../pages/sso-callback';
import Logout from '../pages/Logout';
import NotFound from '../pages/NotFound';
import AccessDenied from '../pages/AccessDenied';
import ChatV2 from '../pages/ChatV2';


const AppContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const [initializing, setInitializing] = useState(true);
  
  // Initialize authentication on app startup
  useEffect(() => {
    const initAuth = async () => {
      try {
        await dispatch(initializeAuth());
      } catch (error) {
        console.error('Auth initialization failed:', error);
      } finally {
        setInitializing(false);
      }
    };
    
    initAuth();
  }, [dispatch]);
  
  // Show loading screen during initial auth check
  if (initializing) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/sso-callback" element={<SsoCallback />} />
      <Route path="/logout" element={<Logout />} />


      {/* Getting Started Route (outside ANIS) */}
      <Route path="getting-started" element={
        <UserRoute>
          <DashboardLayout>
            <GettingStarted />
          </DashboardLayout>
        </UserRoute>
      } />

      {/* Customer Profiling - dedicated route */}
      <Route path="cp/*" element={
        <UserRoute>
          <DashboardLayout>
            <CustomerProfiling />
          </DashboardLayout>
        </UserRoute>
      } />

      {/* ANIS Routes (AGRANA News Intelligence System) */}
      <Route path="anis/dashboard" element={
        <UserRoute>
          <DashboardLayout>
            <UserDashboard />
          </DashboardLayout>
        </UserRoute>
      } />
      <Route path="anis/summaries/:id" element={
        <UserRoute>
          <DashboardLayout>
            <SummaryView />
          </DashboardLayout>
        </UserRoute>
      } />
      <Route path="anis/processes" element={
        <UserRoute>
          <DashboardLayout>
            <ProcessesPage />
          </DashboardLayout>
        </UserRoute>
      } />
      <Route path="anis/chatbot" element={
        <UserRoute>
          <DashboardLayout>
            <ChatV2 />
          </DashboardLayout>
        </UserRoute>
      } />
      <Route path="anis/feedback" element={
        <UserRoute>
          <DashboardLayout>
            <Feedback />
          </DashboardLayout>
        </UserRoute>
      } />
      <Route path="anis/summaries" element={
        <UserRoute>
          <DashboardLayout>
            <SummariesPage />
          </DashboardLayout>
        </UserRoute>
      } />
      <Route path="anis/weekly-summaries" element={
        <UserRoute>
          <DashboardLayout>
            <WeeklySummariesPage />
          </DashboardLayout>
        </UserRoute>
      } />

      {/* Legacy Routes (backward compatibility) */}
      <Route path="dashboard" element={
        <UserRoute>
          <DashboardLayout>
            <UserDashboard />
          </DashboardLayout>
        </UserRoute>
      } />
      <Route path="profile" element={
        <UserRoute>
          <DashboardLayout>
            <Profile />
          </DashboardLayout>
        </UserRoute>
      } />
      <Route path="summaries/:id" element={
        <UserRoute>
          <DashboardLayout>
            <SummaryView />
          </DashboardLayout>
        </UserRoute>
      } />
      <Route path="processes" element={
        <UserRoute>
          <DashboardLayout>
            <ProcessesPage />
          </DashboardLayout>
        </UserRoute>
      } />
      <Route path="chatbot" element={
        <UserRoute>
          <DashboardLayout>
            <ChatV2 />
          </DashboardLayout>
        </UserRoute>
      } />
      <Route path="feedback" element={
        <UserRoute>
          <DashboardLayout>
            <Feedback />
          </DashboardLayout>
        </UserRoute>
      } />
      <Route path="summaries" element={
        <UserRoute>
          <DashboardLayout>
            <SummariesPage />
          </DashboardLayout>
        </UserRoute>
      } />

      {/* Admin Routes */}
      <Route path="admin" element={
        <AdminRoute>
          <DashboardLayout>
            <AdminDashboard />
          </DashboardLayout>
        </AdminRoute>
      } />
      <Route path="admin/users" element={
        <AdminRoute>
          <DashboardLayout>
            <UserManagement />
          </DashboardLayout>
        </AdminRoute>
      } />
      <Route path="admin/monitoring" element={
        <AdminRoute>
          <DashboardLayout>
            <MonitoringDashboard />
          </DashboardLayout>
        </AdminRoute>
      } />

      {/* Access Denied Route */}
      <Route path="access-denied" element={<AccessDenied />} />

      {/* 404 Route */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppContent;